import { ReloadOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Space, Tooltip } from 'antd'
import lowerCase from 'lodash/lowerCase'
import sortBy from 'lodash/sortBy'
import startCase from 'lodash/startCase'
import { useCallback, useMemo, useState, type JSX, type ReactNode } from 'react'

import { SettingsCard } from '../../Settings'
import {
  ONBOARDING_MESSAGE_TITLES,
  INTRO_WITH_FAN_REGISTRATION_URL_LABEL,
  SUBSCRIPTION_CONFIRMATION_LABEL,
} from '../constants'

import { EditLegalOnboardingMessage } from './EditLegalOnboardingMessage'
import { EditOnboardingMessage } from './EditOnboardingMessage'
import { EditSubscriptionMessage } from './EditSubscriptionMessage'
import { StyledMessageBody, StyledMessageHeader, StyledMessageItem, StyledMessageList } from './styled'
import { WhatsAppStatusTag } from './WhatsAppStatusTag'

import { Client } from 'api/clients'
import { QUERIES } from 'constants/queries'

const RenderDictionary = {
  [INTRO_WITH_FAN_REGISTRATION_URL_LABEL]: EditLegalOnboardingMessage,
  [SUBSCRIPTION_CONFIRMATION_LABEL]: EditSubscriptionMessage,
  Default: EditOnboardingMessage,
}

export const EditOnboardingMessagesList = ({
  title,
  client,
  messageLabels,
  actions,
}: {
  title: string
  client: Client
  messageLabels: string[]
  actions?: ReactNode
}): JSX.Element => {
  const queryClient = useQueryClient()
  const messages = useMemo(
    () =>
      sortBy(
        client.onboardingMessages.filter((message) => messageLabels.includes(message.label)),
        'label',
      ),
    [messageLabels, client],
  )

  const [isLoadingClient, setIsLoadingClient] = useState(false)
  const reloadClient = useCallback(async () => {
    setIsLoadingClient(true)
    await queryClient.refetchQueries([QUERIES.CLIENT, { id: client.id }])
    setIsLoadingClient(false)
  }, [queryClient, client])

  return (
    <SettingsCard
      extra={
        <Space>
          {actions}
          <Tooltip title="Reload messages and statuses">
            <Button
              aria-label="Reload Messages"
              icon={<ReloadOutlined />}
              loading={isLoadingClient}
              onClick={() => !isLoadingClient && reloadClient()}
              shape="circle"
            ></Button>
          </Tooltip>
        </Space>
      }
      title={title}
    >
      <StyledMessageList>
        {messages.map((message) => {
          const Render = RenderDictionary[message.label] || RenderDictionary.Default
          return (
            <StyledMessageItem key={message.label}>
              <StyledMessageHeader>
                <h4>{ONBOARDING_MESSAGE_TITLES[message.label] || startCase(lowerCase(message.label))}</h4>
                {message.campaignTemplateStatus && <WhatsAppStatusTag status={message.campaignTemplateStatus} />}
              </StyledMessageHeader>
              <StyledMessageBody>
                <Render client={client} message={message} messages={client.onboardingMessages} />
              </StyledMessageBody>
            </StyledMessageItem>
          )
        })}
      </StyledMessageList>
    </SettingsCard>
  )
}
